import { Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, shareReplay, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { RootState } from '../../../reducers';
import { getWatchedUsers } from '../../../selectors/user.selectors';
import { initializeWatchedUsers, watchUsers } from '../../../actions/user.actions';
import { User } from '../../../models/user';
import { Plan } from '../../../models/plan';
import { getPlans } from '../../../selectors/plan.selectors';
import { findPlans, initializeFindPlansState } from '../../../actions/plan.actions';
import { navigate, setBrowserTitle, setTitle, unsubscribeWatchedProps } from '../../../actions/core.actions';
import { enter } from '../../../resources/animations';
import { RoutingPathResolver } from '../../../app-routing-path-resolver';
import {
  ADD_DAIGAKU_DIALOG_HEIGHT,
  ADD_DAIGAKU_DIALOG_WIDTH,
  DELETE_DAIGAKU_DIALOG_WIDTH,
  DIALOG_ZERO_PADDING_PANEL_CLASS,
  EDIT_DAIGAKU_DIALOG_HEIGHT,
  EDIT_DAIGAKU_DIALOG_WIDTH,
  DAIGAKU_ORGANIZATION_ID,
  OTHER_DAIGAKU_CODE,
  PlanStatuses,
  UnsubscribeTarget
} from '../../../resources/config';
import { CurrentDateTime } from '../../../models/current-date-time';
import { getCurrentDateTime as getCurrentDateTimeSelector } from '../../../selectors/current-date-time.selectors';
import { getCurrentDateTime, initializeGetCurrentDateTime } from '../../../actions/current-date-time.actions';
import { PlanUtil, PriorityRecord } from '../../../utils/plan-util';
import { Daigaku, UpdateDaigakuData } from 'src/app/models/daigaku';
import { getDaigakus } from '../../../selectors/daigaku.selectors';
import { findDaigakus, initializeFindDaigakusState } from 'src/app/actions/daigaku.actions';
import { DeleteDaigakuDialogComponent, DeleteDaigakuDialogData } from '../delete-daigaku-dialog/delete-daigaku-dialog.component';
import { MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig } from '@angular/material/legacy-dialog';
import { Log } from 'src/app/utils/log';
import { AddDaigakuDialogComponent } from '../add-daigaku-dialog/add-daigaku-dialog.component';
import { EditDaigakuDialogComponent, EditDaigakuDialogData } from '../edit-daigaku-dialog/edit-daigaku-dialog.component';
import { AccountMenuService } from 'src/app/services/account-menu.service';
import { DaigakuUtil } from 'src/app/utils/daigaku-util';

interface DaigakuData {
  id: string;
  daigakuCode: string;
  daigakuName: string;
  daigakuSubName: string;
  prefectureId: string;
  planStatus: string;
}

@Component({
  selector: 'app-daigakus',
  templateUrl: './daigakus.component.html',
  styleUrls: ['./daigakus.component.scss'],
  animations: [enter]
})
export class DaigakusComponent implements OnInit, OnDestroy {
  sortDirection: 'asc' | 'desc' = 'desc';
  daigakusData$: Observable<DaigakuData[]>;
  planUnassignedStatus: string = PlanStatuses.UNASSIGNED;
  planActiveStatus: string = PlanStatuses.ACTIVE;
  planEndedStatus: string = PlanStatuses.ENDED;
  displayedColumns: string[] = ['daigakuCode', 'daigakuName', 'planStatus', 'manage', 'edit', 'remove'];
  otherDaigakuCode: string = OTHER_DAIGAKU_CODE;

  menu = this.accountMenuService.menus;

  private LOG_SOURCE = this.constructor.name;
  private title = '大学一覧';

  constructor(private store: Store<RootState>, private dialog: MatDialog, private accountMenuService: AccountMenuService) {}

  ngOnInit() {
    this.store.dispatch(setBrowserTitle({ subTitle: this.title }));
    setTimeout(() => this.store.dispatch(setTitle({ title: this.title })));

    this.setUp();
  }

  ngOnDestroy() {
    this.store.dispatch(initializeFindDaigakusState());
    this.store.dispatch(initializeWatchedUsers());
    this.store.dispatch(initializeFindPlansState());
    this.store.dispatch(initializeGetCurrentDateTime());
    this.store.dispatch(unsubscribeWatchedProps({ target: UnsubscribeTarget.WATCH_USERS }));
  }

  plans(daigakuCode: string) {
    this.store.dispatch(navigate({ url: RoutingPathResolver.resolvePlans(DAIGAKU_ORGANIZATION_ID, daigakuCode) }));
  }

  addDaigaku() {
    const config: MatDialogConfig = {
      height: ADD_DAIGAKU_DIALOG_HEIGHT,
      width: ADD_DAIGAKU_DIALOG_WIDTH,
      panelClass: DIALOG_ZERO_PADDING_PANEL_CLASS,
      disableClose: true
    };
    this.dialog
      .open(AddDaigakuDialogComponent, config)
      .afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result) this.store.dispatch(findDaigakus({}));
      });
  }

  editDaigaku(daigaku: DaigakuData) {
    const data: UpdateDaigakuData = {
      id: daigaku.id,
      daigakuName: daigaku.daigakuName,
      daigakuSubName: daigaku.daigakuSubName,
      daigakuCode: daigaku.daigakuCode,
      prefectureId: daigaku.prefectureId
    };

    const config: MatDialogConfig<EditDaigakuDialogData> = {
      height: EDIT_DAIGAKU_DIALOG_HEIGHT,
      width: EDIT_DAIGAKU_DIALOG_WIDTH,
      panelClass: DIALOG_ZERO_PADDING_PANEL_CLASS,
      data: { daigaku: data },
      disableClose: true
    };
    this.dialog
      .open(EditDaigakuDialogComponent, config)
      .afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result) this.store.dispatch(findDaigakus({}));
      });
  }

  removeDaigaku(daigaku: DaigakuData) {
    Log.debug(this.LOG_SOURCE, `次の大学を削除します: `, daigaku);
    const data: DeleteDaigakuDialogData = { id: daigaku.id, daigakuCode: daigaku.daigakuCode, daigakuName: daigaku.daigakuName };
    const config: MatDialogConfig = {
      width: DELETE_DAIGAKU_DIALOG_WIDTH,
      panelClass: DIALOG_ZERO_PADDING_PANEL_CLASS,
      autoFocus: false,
      data,
      disableClose: true
    };
    this.dialog
      .open(DeleteDaigakuDialogComponent, config)
      .afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result) this.store.dispatch(findDaigakus({}));
      });
  }

  breadcrumbsClickHandler(key) {
    switch (key) {
      case 'admin':
        this.store.dispatch(navigate({ url: RoutingPathResolver.resolveAdmin() }));
        break;
    }
  }

  menuClickHandler(key) {
    this.accountMenuService.menuClickHandler(key);
  }

  getDaigakuName(daigaku: Daigaku): string {
    return DaigakuUtil.getDisplayDaigakuName(daigaku);
  }

  private setUp() {
    const daigakus$: Observable<Daigaku[]> = this.store.select(getDaigakus).pipe(
      filter(it => it != null),
      shareReplay(1)
    );

    const users$: Observable<User[]> = this.store.select(getWatchedUsers).pipe(
      filter(it => it != null),
      shareReplay(1)
    );

    const plans$: Observable<Plan[]> = this.store.select(getPlans).pipe(
      filter(it => it != null),
      shareReplay(1)
    );

    const currentDateTime$: Observable<CurrentDateTime> = this.store.select(getCurrentDateTimeSelector).pipe(
      filter(it => it != null),
      shareReplay(1)
    );

    this.daigakusData$ = combineLatest([daigakus$, users$, plans$, currentDateTime$]).pipe(
      map(([daigakus, users, plans, currentDateTime]) => {
        const mapedDaigakus = daigakus
          .map(daigaku => {
            const daigakuPlans: Plan[] = plans.filter(
              plan => plan.organization === DAIGAKU_ORGANIZATION_ID && plan.schoolId === daigaku.daigakuCode
            );
            return {
              id: daigaku.id,
              daigakuCode: daigaku.daigakuCode,
              daigakuName: daigaku.daigakuName,
              daigakuSubName: daigaku.daigakuSubName,
              prefectureId: daigaku.prefectureId,
              planStatus: this.getDaigakuPlanStatus(daigakuPlans, currentDateTime)
            };
          })
          .filter(daigaku => daigaku !== null);

        mapedDaigakus.sort((a, b) => {
          if (a.daigakuCode === b.daigakuCode) {
            return 0;
          }
          return a.daigakuCode > b.daigakuCode ? 1 : -1;
        });

        return mapedDaigakus;
      }),
      shareReplay(1)
    );

    this.store.dispatch(watchUsers({ sortDirection: this.sortDirection }));
    this.store.dispatch(findPlans({}));
    this.store.dispatch(getCurrentDateTime());
    this.store.dispatch(findDaigakus({}));
  }

  private getDaigakuPlanStatus(plans: Plan[], currentDateTime: CurrentDateTime): string {
    if (plans.length === 0) return this.planUnassignedStatus;

    return plans.find(plan => {
      const priorityRecord: PriorityRecord = PlanUtil.getPriorityRecord(plan.records, currentDateTime);
      return priorityRecord.status === PlanStatuses.ACTIVE || priorityRecord.status === PlanStatuses.RESERVE;
    })
      ? this.planActiveStatus
      : this.planEndedStatus;
  }
}
